import { useMutation, useQueryClient } from "@tanstack/react-query";
import { claimFreeTrial } from "src/api/editionCategory";
import { CATEGORY_QUERY_KEYS } from "src/components/system/category/hooks/useCategoryMenu";
import { QUERY_KEYS } from "../constants";
/** 쿼리 타입별 predicate 생성 */ const createQueryPredicate = (isGrouped)=>(query)=>query.queryKey[0] === QUERY_KEYS.CATEGORY_PRODUCTS && (isGrouped ? query.queryKey.includes("grouped") : !query.queryKey.includes("grouped"));
/** 시스템 카테고리 쿼리 predicate */ const systemCategoryPredicate = (query)=>query.queryKey[0] === CATEGORY_QUERY_KEYS.CATEGORY_PRODUCTS;
/** 에디션 카테고리 캐시 업데이트 */ const updateProductCaches = (queryClient, updater)=>{
    // 일반 상품 리스트
    queryClient.getQueriesData({
        predicate: createQueryPredicate(false)
    }).forEach((param)=>{
        let [queryKey, data] = param;
        if (data === null || data === void 0 ? void 0 : data.items) {
            queryClient.setQueryData(queryKey, {
                ...data,
                items: data.items.map(updater)
            });
        }
    });
    // 그룹화된 상품 리스트
    queryClient.getQueriesData({
        predicate: createQueryPredicate(true)
    }).forEach((param)=>{
        let [queryKey, data] = param;
        if (data === null || data === void 0 ? void 0 : data.groups) {
            queryClient.setQueryData(queryKey, {
                ...data,
                groups: data.groups.map((group)=>({
                        ...group,
                        items: group.items.map(updater)
                    }))
            });
        }
    });
};
/** 시스템 카테고리 캐시 업데이트 */ const updateSystemCategoryCaches = (queryClient, updater)=>{
    queryClient.getQueriesData({
        predicate: systemCategoryPredicate
    }).forEach((param)=>{
        let [queryKey, data] = param;
        if (data === null || data === void 0 ? void 0 : data.items) {
            queryClient.setQueryData(queryKey, {
                ...data,
                items: data.items.map(updater)
            });
        }
    });
};
/** 캐시 스냅샷 저장 */ const saveCacheSnapshots = (queryClient)=>{
    const productSnapshots = queryClient.getQueriesData({
        predicate: createQueryPredicate(false)
    }).map((param)=>{
        let [queryKey, data] = param;
        return {
            queryKey,
            data
        };
    });
    const groupedSnapshots = queryClient.getQueriesData({
        predicate: createQueryPredicate(true)
    }).map((param)=>{
        let [queryKey, data] = param;
        return {
            queryKey,
            data
        };
    });
    const categoryInfoSnapshots = queryClient.getQueriesData({
        queryKey: [
            QUERY_KEYS.EDITION_CATEGORY_INFO
        ]
    }).map((param)=>{
        let [queryKey, data] = param;
        return {
            queryKey,
            data
        };
    });
    const systemCategorySnapshots = queryClient.getQueriesData({
        predicate: systemCategoryPredicate
    }).map((param)=>{
        let [queryKey, data] = param;
        return {
            queryKey,
            data
        };
    });
    return {
        productSnapshots,
        groupedSnapshots,
        categoryInfoSnapshots,
        systemCategorySnapshots
    };
};
/** 캐시 롤백 */ const rollbackCache = (queryClient, context)=>{
    context.productSnapshots.forEach((param)=>{
        let { queryKey , data  } = param;
        queryClient.setQueryData(queryKey, data);
    });
    context.groupedSnapshots.forEach((param)=>{
        let { queryKey , data  } = param;
        queryClient.setQueryData(queryKey, data);
    });
    context.categoryInfoSnapshots.forEach((param)=>{
        let { queryKey , data  } = param;
        queryClient.setQueryData(queryKey, data);
    });
    context.systemCategorySnapshots.forEach((param)=>{
        let { queryKey , data  } = param;
        queryClient.setQueryData(queryKey, data);
    });
};
var _existingFreeTrial_remainingToday, _overrides_remainingToday, _existingFreeTrial_promotionId, _existingFreeTrial_dailyLimit;
/** freeTrial 상태 생성 헬퍼 */ const createOwnedFreeTrial = (existingFreeTrial, overrides)=>{
    return {
        eligible: false,
        reason: "already_owned",
        remainingToday: (_overrides_remainingToday = overrides.remainingToday) !== null && _overrides_remainingToday !== void 0 ? _overrides_remainingToday : ((_existingFreeTrial_remainingToday = existingFreeTrial === null || existingFreeTrial === void 0 ? void 0 : existingFreeTrial.remainingToday) !== null && _existingFreeTrial_remainingToday !== void 0 ? _existingFreeTrial_remainingToday : 1) - 1,
        promotionId: (_existingFreeTrial_promotionId = existingFreeTrial === null || existingFreeTrial === void 0 ? void 0 : existingFreeTrial.promotionId) !== null && _existingFreeTrial_promotionId !== void 0 ? _existingFreeTrial_promotionId : null,
        dailyLimit: (_existingFreeTrial_dailyLimit = existingFreeTrial === null || existingFreeTrial === void 0 ? void 0 : existingFreeTrial.dailyLimit) !== null && _existingFreeTrial_dailyLimit !== void 0 ? _existingFreeTrial_dailyLimit : 0
    };
};
var _existingFreeTrial_promotionId1, _existingFreeTrial_dailyLimit1;
/** 일일 한도 초과 상태 생성 헬퍼 */ const createDailyLimitExceededFreeTrial = (existingFreeTrial)=>{
    return {
        eligible: false,
        reason: "daily_limit_exceeded",
        remainingToday: 0,
        promotionId: (_existingFreeTrial_promotionId1 = existingFreeTrial === null || existingFreeTrial === void 0 ? void 0 : existingFreeTrial.promotionId) !== null && _existingFreeTrial_promotionId1 !== void 0 ? _existingFreeTrial_promotionId1 : null,
        dailyLimit: (_existingFreeTrial_dailyLimit1 = existingFreeTrial === null || existingFreeTrial === void 0 ? void 0 : existingFreeTrial.dailyLimit) !== null && _existingFreeTrial_dailyLimit1 !== void 0 ? _existingFreeTrial_dailyLimit1 : 0
    };
};
/** 모든 무료체험 가능 상품을 일일 한도 초과 상태로 업데이트 */ const updateAllProductsToLimitExceeded = (queryClient)=>{
    const editionUpdater = (product)=>{
        if (!product.freeTrial || product.freeTrial.reason === "already_owned") return product;
        if (product.freeTrial.eligible || product.freeTrial.reason === "not_logged_in") {
            return {
                ...product,
                freeTrial: createDailyLimitExceededFreeTrial(product.freeTrial)
            };
        }
        return product;
    };
    const systemUpdater = (item)=>{
        if (!item.freeTrial || item.freeTrial.reason === "already_owned") return item;
        if (item.freeTrial.eligible || item.freeTrial.reason === "not_logged_in") {
            return {
                ...item,
                freeTrial: createDailyLimitExceededFreeTrial(item.freeTrial)
            };
        }
        return item;
    };
    updateProductCaches(queryClient, editionUpdater);
    updateSystemCategoryCaches(queryClient, systemUpdater);
};
/**
 * 무료체험 자료 받기 mutation hook
 * - 낙관적 업데이트: API 호출 전에 즉시 UI 업데이트, 실패 시 롤백
 * - 에디션 카테고리 + 시스템 카테고리 캐시 모두 업데이트
 */ const useClaimFreeTrial = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: claimFreeTrial,
        onMutate: async (param)=>{
            let { productId  } = param;
            await queryClient.cancelQueries({
                queryKey: [
                    QUERY_KEYS.CATEGORY_PRODUCTS
                ]
            });
            await queryClient.cancelQueries({
                queryKey: [
                    QUERY_KEYS.EDITION_CATEGORY_INFO
                ]
            });
            await queryClient.cancelQueries({
                queryKey: [
                    CATEGORY_QUERY_KEYS.CATEGORY_PRODUCTS
                ]
            });
            const context = saveCacheSnapshots(queryClient);
            // 에디션 카테고리 낙관적 업데이트
            updateProductCaches(queryClient, (product)=>product.id === productId ? {
                    ...product,
                    freeTrial: createOwnedFreeTrial(product.freeTrial, {}),
                    licensePlan: "view",
                    isFreeOwned: true
                } : product);
            // 시스템 카테고리 낙관적 업데이트 (id가 string | number이므로 양쪽 비교)
            updateSystemCategoryCaches(queryClient, (item)=>String(item.id) === String(productId) ? {
                    ...item,
                    freeTrial: createOwnedFreeTrial(item.freeTrial, {}),
                    licensePlan: "view"
                } : item);
            // categoryInfo의 remainingToday도 -1 차감
            let newRemainingToday = 0;
            queryClient.getQueriesData({
                queryKey: [
                    QUERY_KEYS.EDITION_CATEGORY_INFO
                ]
            }).forEach((param)=>{
                let [queryKey, data] = param;
                if (data === null || data === void 0 ? void 0 : data.freeTrialPromotion) {
                    var _data_freeTrialPromotion_remainingToday;
                    newRemainingToday = Math.max(0, ((_data_freeTrialPromotion_remainingToday = data.freeTrialPromotion.remainingToday) !== null && _data_freeTrialPromotion_remainingToday !== void 0 ? _data_freeTrialPromotion_remainingToday : 0) - 1);
                    queryClient.setQueryData(queryKey, {
                        ...data,
                        freeTrialPromotion: {
                            ...data.freeTrialPromotion,
                            remainingToday: newRemainingToday
                        }
                    });
                }
            });
            // remainingToday가 0이 되면 다른 무료체험 가능 상품들도 일일 한도 초과로 업데이트
            if (newRemainingToday === 0) {
                updateAllProductsToLimitExceeded(queryClient);
            }
            return context;
        },
        onError: (_error, _variables, context)=>{
            if (context) rollbackCache(queryClient, context);
        },
        onSuccess: (param, param1)=>{
            let { license , remainingToday  } = param, { productId  } = param1;
            const licensePlan = license.purchaseOption === "view" ? "view" : "download";
            // 에디션 카테고리 캐시 업데이트
            updateProductCaches(queryClient, (product)=>product.id === productId ? {
                    ...product,
                    freeTrial: createOwnedFreeTrial(product.freeTrial, {
                        remainingToday
                    }),
                    licensePlan,
                    licenseId: license.id,
                    isFreeOwned: true
                } : product);
            // 시스템 카테고리 캐시 업데이트
            updateSystemCategoryCaches(queryClient, (item)=>String(item.id) === String(productId) ? {
                    ...item,
                    freeTrial: createOwnedFreeTrial(item.freeTrial, {
                        remainingToday
                    }),
                    licensePlan,
                    licenseId: license.id
                } : item);
            // categoryInfo의 remainingToday도 서버 응답값으로 업데이트
            queryClient.getQueriesData({
                queryKey: [
                    QUERY_KEYS.EDITION_CATEGORY_INFO
                ]
            }).forEach((param)=>{
                let [queryKey, data] = param;
                if (data === null || data === void 0 ? void 0 : data.freeTrialPromotion) {
                    queryClient.setQueryData(queryKey, {
                        ...data,
                        freeTrialPromotion: {
                            ...data.freeTrialPromotion,
                            remainingToday
                        }
                    });
                }
            });
            // remainingToday가 0이면 다른 무료체험 가능 상품들도 일일 한도 초과로 업데이트
            if (remainingToday === 0) {
                updateAllProductsToLimitExceeded(queryClient);
            }
        }
    });
};
export default useClaimFreeTrial;
